import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, LoadingIndicator, StateScreen } from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';

const PENDING_KEY = 'pendingInvite';

function readPending(): string | null {
  try {
    return sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

function writePending(token: string | null): void {
  try {
    if (token === null) sessionStorage.removeItem(PENDING_KEY);
    else sessionStorage.setItem(PENDING_KEY, token);
  } catch {
    // Storage can be blocked. The token then lives only in this component's memory.
  }
}

/**
 * `/join/:token`: the token is exchanged on page load. It moves out of the address bar
 * (and history) at once, into this tab's session storage, and is used for one accept call.
 */
export function JoinScreen() {
  const { t } = useTranslation();
  const { token: fromPath } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const memory = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (fromPath !== undefined) {
      memory.current = fromPath;
      writePending(fromPath);
      void navigate('/join', { replace: true });
    }
  }, [fromPath, navigate]);

  const token = memory.current ?? readPending();
  const accepting = useRef(false);

  useEffect(() => {
    if (fromPath !== undefined || auth.status !== 'authenticated' || token === null) return;
    if (accepting.current) return;
    accepting.current = true;
    void (async () => {
      try {
        const result = await api<{ accessToken: string }>('/invites/accept', {
          method: 'POST',
          body: { token },
        });
        writePending(null);
        memory.current = null;
        await auth.adopt(result.accessToken);
        void navigate('/', { replace: true });
      } catch {
        writePending(null);
        setFailed(true);
      }
    })();
  }, [fromPath, auth, token, navigate]);

  if (token === null && !failed && fromPath === undefined && auth.status !== 'loading')
    return <Navigate to="/" replace />;
  if (auth.status === 'anonymous' && fromPath === undefined)
    return <Navigate to="/signin?next=%2Fjoin" replace />;
  return (
    <div className="sign-page">
      {failed ? (
        <StateScreen
          kind="expired"
          title={t('join.invalidTitle')}
          action={
            <Button onClick={() => void navigate('/', { replace: true })}>
              {t('join.continue')}
            </Button>
          }
        >
          {t('join.invalid')}
        </StateScreen>
      ) : (
        <StateScreen kind="empty" compact title={t('join.accepting')}>
          <LoadingIndicator label={t('app.loading')} />
        </StateScreen>
      )}
    </div>
  );
}
